import React from 'react';
import Box from '@material-ui/core/Box';
import NumberInput from '@/se/components/inputs/NumberInput';
import ToggleInput from '@/se/components/inputs/ToogleInput';
import { withLabel } from '@/se/components/Label';
import { withProps } from 'recompose';
import graphqlSelectInput from '../../graphqlSelectInput';
import { externalScheduleProviders, timezoneOptions } from '@/graph/hospitals';
import { mapProps } from 'recompose';
import get from 'lodash/get';
import styled from 'styled-components';
import { HospitalFormValue } from '../types';

const LabeledMinBracelets = withLabel('Minimum Active Bracelets')(NumberInput) as any;
const LabeledQuestionnaireMonths = withLabel('Questionnaire valid in months for repeated patients')(NumberInput) as any;
const LabeledPasswordRefreshDays = withLabel('Period in days after which user is forced to change password')(
  NumberInput
) as any;
const LabeledSessionTimeout = withLabel('Session Expiration Timeout (in seconds)')(NumberInput) as any;

const ScheduleProviderInput = withProps({
  isClearable: true,
})(
  graphqlSelectInput({
    entityName: 'externalScheduleProvider',
    label: 'External Schedule CSV Provider',
    placeholder: 'Select',
    graphqlList: externalScheduleProviders,
    transformOptions: (options: any[]) => options.map(opt => ({ id: opt, name: opt })),
  })
) as any;

const TimezoneInput = (graphqlSelectInput as any)({
  entityName: 'timezone',
  label: 'Timezone',
  placeholder: 'Select',
  graphqlList: timezoneOptions,
  getOptionValue: (option: any) => (option ? option.value : undefined),
  transformOptions: (options: any[]) => options.map(({ id, name }: any) => ({ id, name })),
});

const MappedTimezoneInput = mapProps((props: any) => ({
  ...props,
  value: get(props, 'value.id', get(props, 'value')),
}))(TimezoneInput) as any;

const GenericParserLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 0;

  .label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
  }

  .badge {
    background: rgba(0, 200, 150, 0.2);
    color: rgb(0, 200, 150);
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
  }
`;

interface SettingsTabProps {
  value: HospitalFormValue;
  onChange: (value: HospitalFormValue) => void;
  disabled?: boolean;
}

const SettingsTab: React.FC<SettingsTabProps> = ({ value, onChange, disabled }) => {
  const handleField = (field: keyof HospitalFormValue) => (val: any) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <Box display="flex" flexDirection="column" maxWidth={600}>
      <LabeledMinBracelets
        value={value.minBracelets}
        onChange={handleField('minBracelets')}
        disabled={disabled}
        min={1}
      />
      <LabeledQuestionnaireMonths
        value={value.questionnaireValidMonths}
        onChange={handleField('questionnaireValidMonths')}
        disabled={disabled}
        min={0}
        required
      />
      <LabeledPasswordRefreshDays
        value={value.passwordRefreshDays}
        onChange={handleField('passwordRefreshDays')}
        disabled={disabled}
        min={0}
      />
      <LabeledSessionTimeout
        value={value.sessionTimeout}
        onChange={handleField('sessionTimeout')}
        disabled={disabled}
        min={60}
      />

      <Box mt={2} mb={2}>
        {value.genericCsvParserConfig ? (
          <GenericParserLabel>
            <span className="label">External Schedule CSV Provider</span>
            <div className="value">
              <span>{value.genericCsvParserConfig}</span>
              <span className="badge">Generic CSV Parser</span>
            </div>
          </GenericParserLabel>
        ) : (
          <ScheduleProviderInput
            value={value.scheduleProvider}
            onChange={handleField('scheduleProvider')}
            disabled={disabled}
          />
        )}
        {value.genericHl7ParserConfig && (
          <GenericParserLabel>
            <span className="label">HL7 Schedule Provider</span>
            <div className="value">
              <span>{value.genericHl7ParserConfig}</span>
              <span className="badge">Generic HL7 Parser</span>
            </div>
          </GenericParserLabel>
        )}
      </Box>

      <MappedTimezoneInput value={value.timezone} onChange={handleField('timezone')} disabled={disabled} />

      <Box mt={2}>
        <ToggleInput
          label="Exchange Only"
          value={value.exchangeOnly || false}
          onChange={handleField('exchangeOnly')}
          isDisabled={disabled}
        />
      </Box>
      <Box mt={1}>
        <ToggleInput
          label="Omit Able To Visit"
          value={value.omitAbleToVisit || false}
          onChange={handleField('omitAbleToVisit')}
          isDisabled={disabled}
        />
      </Box>
    </Box>
  );
};

export default SettingsTab;
